import type { ReactElement } from 'react'

import { useTheme } from '@emotion/react'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { TextButton } from '@twistopayments/design-system/components/atoms/button'
import {
  Headline,
  HeadlineIcon,
  HeadlineSubtitle,
  HeadlineTitle,
} from '@twistopayments/design-system/components/atoms/headline'
import { Hidden } from '@twistopayments/design-system/components/atoms/hidden'
import {
  Modal,
  ModalContent,
  ModalNavigation,
} from '@twistopayments/design-system/components/atoms/modal'
import { Typography } from '@twistopayments/design-system/components/atoms/typography'
import {
  Step,
  StepContent,
  StepHeadline,
  StepHeadlineTitle,
  StepIcon,
  Stepper,
} from '@twistopayments/design-system/components/molecules/stepper'
import { BigTwistoIcon } from '@twistopayments/design-system/icons/big'

import * as styles from './pay-in-n-explanation-modal.styles'

type Props = {
  onClose: () => void
  opened: boolean
  maxInstallments: number
}

const steps: ReactElement[] = [
  <Trans key="one" id="psp.explanationModal.payInN.item.one" />,
  <Trans key="two" id="psp.explanationModal.payInN.item.two" />,
  <Trans key="three" id="psp.explanationModal.payInN.item.three" />,
]

export const PayInNExplanationModal = ({
  onClose,
  opened,
  maxInstallments,
}: Props) => {
  const theme = useTheme()

  return (
    <Modal
      noPadding
      aria-label={/*i18n*/ i18n._('psp.explanationModal.title')}
      opened={opened}
      onClickOutside={onClose}
    >
      <ModalNavigation
        modalCloser={{
          onClick: onClose,
        }}
        title={<Trans id="psp.explanationModal.payInN.title" />}
      />

      <ModalContent css={styles.modalContent}>
        <Headline>
          <HeadlineIcon>
            <BigTwistoIcon
              color="secondaryMainContrast"
              height="100%"
              width="100%"
            />
          </HeadlineIcon>
          <HeadlineTitle>
            <Trans id="psp.explanationModal.payInN.perex.title" />
          </HeadlineTitle>
          <HeadlineSubtitle>
            <Trans
              id="psp.explanationModal.payInN.perex"
              values={{ maxMonths: maxInstallments }}
            />
          </HeadlineSubtitle>
        </Headline>

        <Stepper
          connectorColor={theme.palette.secondary.mainContrast}
          iconRingColor={theme.palette.secondary.mainContrast}
        >
          {steps.map((label, index) => (
            <Step key={index}>
              <StepIcon color="primary" />
              <StepContent>
                <StepHeadline>
                  <StepHeadlineTitle>
                    <Typography color="textSecondary">{label}</Typography>
                  </StepHeadlineTitle>
                </StepHeadline>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        <Hidden smUp>
          <TextButton fullWidth color="secondary" onClick={onClose}>
            <Trans id="modal.slide.goBack" />
          </TextButton>
        </Hidden>
      </ModalContent>
    </Modal>
  )
}
